import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Gift,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  CreditCard
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { useNotification } from '../hooks/useNotification';

const USAGE_PLANS = {
  starter: {
    id: "starter",
    name: "Starter Plan",
    price: 29,
    currency: "₹",
    limits: {
      firebase_operations: 3000,
      storage_mb: 100
    },
    description: "Perfect for small businesses"
  },
  basic: {
    id: "basic",
    name: "Basic Plan",
    price: 49,
    currency: "₹",
    limits: {
      firebase_operations: 10000,
      storage_mb: 300
    },
    description: "Great for growing businesses"
  },
  pro: {
    id: "pro",
    name: "Pro Plan",
    price: 199,
    currency: "₹",
    limits: {
      firebase_operations: 50000,
      storage_mb: 1024
    },
    description: "Advanced for scaling businesses"
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise Plan",
    price: 399,
    currency: "₹",
    limits: {
      firebase_operations: 200000,
      storage_mb: 5120
    },
    description: "Unlimited for large businesses"
  }
};

const PlanUpgradeUser = ({ db, onClose }) => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const notify = useNotification();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgradeBenefits, setUpgradeBenefits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (companyId) {
      loadCurrentSubscription();
    }
  }, [companyId]);

  useEffect(() => {
    if (currentSubscription && selectedPlan) {
      calculateUpgradeBenefits();
    }
  }, [currentSubscription, selectedPlan]);

  const loadCurrentSubscription = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
      const subscriptionSnap = await getDoc(subscriptionRef);

      if (subscriptionSnap.exists()) {
        setCurrentSubscription(subscriptionSnap.data());
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      setMessage({ type: 'error', text: 'Error loading subscription' });
    } finally {
      setLoading(false);
    }
  };

  const calculateUpgradeBenefits = () => {
    const basePlan = currentSubscription.basePlan || currentSubscription;
    const newPlan = USAGE_PLANS[selectedPlan];

    // Calculate remaining value
    const now = new Date();
    const resetDate = new Date(basePlan.nextResetDate);
    const startDate = new Date(basePlan.startDate);

    const totalDays = Math.ceil((resetDate - startDate) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, Math.ceil((resetDate - now) / (1000 * 60 * 60 * 24)));

    const usage = currentSubscription.currentMonthUsage || {};
    const limits = basePlan.limits || {};

    const operationsBonus = Math.max(0, (limits.firebase_operations || 0) - (usage.firebase_operations || 0));
    const newValidityDays = 30 + daysRemaining;

    const validUntil = new Date(now);
    validUntil.setDate(validUntil.getDate() + newValidityDays);

    setUpgradeBenefits({
      operationsBonus,
      timeBonusDays: daysRemaining,
      newValidityDays,
      validUntil: validUntil.toISOString(),
      totalOperations: newPlan.limits.firebase_operations + operationsBonus,
      chargeAmount: newPlan.price
    });
  };

  const handleUpgradeClick = () => {
    setShowConfirmModal(true);
  };

  const handlePayment = async (paymentMethod) => {
    if (!selectedPlan) return;

    try {
      setLoading(true);

      const metadata = {
        companyId: companyId,
        type: 'upgrade',
        fromPlan: currentSubscription.basePlan?.planId || currentSubscription.planId,
        toPlan: selectedPlan
      };

      if (paymentMethod === 'razorpay') {
        await initializeRazorpayPayment(upgradeBenefits.chargeAmount, metadata);
      } else if (paymentMethod === 'stripe') {
        await initializeStripePayment(upgradeBenefits.chargeAmount, metadata);
      }

      setMessage({
        type: 'success',
        text: 'Upgrade payment initiated!'
      });

    } catch (error) {
      console.error('Error initiating payment:', error);
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const initializeRazorpayPayment = async (amount, metadata) => {
    console.log('Razorpay payment:', { amount, metadata });
    notify.info(`Razorpay payment gateway will process ₹${amount}. This feature will be enabled soon.`, {
      title: 'Payment Gateway',
      duration: 6000
    });
  };

  const initializeStripePayment = async (amount, metadata) => {
    console.log('Stripe payment:', { amount, metadata });
    notify.info(`Stripe payment gateway will process ₹${amount}. This feature will be enabled soon.`, {
      title: 'Payment Gateway',
      duration: 6000
    });
  };

  const getCurrentPlanId = () => {
    return currentSubscription?.basePlan?.planId || currentSubscription?.planId || 'starter';
  };

  const getAvailableUpgrades = () => {
    const currentPlanId = getCurrentPlanId();
    const planOrder = ['starter', 'basic', 'pro', 'enterprise'];
    const currentIndex = planOrder.indexOf(currentPlanId);

    return planOrder
      .slice(currentIndex + 1)
      .map(planId => USAGE_PLANS[planId]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!currentSubscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6 flex items-center justify-center">
        <div className="text-gray-500">Loading subscription...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Upgrade Your Plan</h1>
          </div>
          <p className="text-gray-600">Get more features with instant benefits</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Current Plan */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Your Current Plan</h3>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-700">
                {currentSubscription.basePlan?.planName || currentSubscription.planName}
              </div>
              <div className="text-gray-600 mt-1">
                {(currentSubscription.basePlan?.limits || currentSubscription.limits)?.firebase_operations?.toLocaleString()} operations/month
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">
                ₹{currentSubscription.basePlan?.price || currentSubscription.price}
              </div>
              <div className="text-sm text-gray-600">/month</div>
            </div>
          </div>
        </div>

        {/* Available Upgrades */}
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Available Upgrades</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {getAvailableUpgrades().map(plan => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`bg-white rounded-xl shadow-lg p-6 cursor-pointer border-2 transition-all ${
                  selectedPlan === plan.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="font-bold text-xl text-gray-800 mb-2">{plan.name}</div>
                <div className="text-3xl font-bold text-blue-700 mb-3">
                  {plan.currency}{plan.price}
                  <span className="text-sm text-gray-600">/month</span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>📊 {plan.limits.firebase_operations.toLocaleString()} operations</div>
                  <div>💾 {plan.limits.storage_mb >= 1024 ? `${(plan.limits.storage_mb / 1024).toFixed(1)} GB` : `${plan.limits.storage_mb} MB`} storage</div>
                </div>
              </div>
            ))}
          </div>

          {getAvailableUpgrades().length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl shadow-lg">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <p className="text-gray-600">You're already on the highest plan!</p>
            </div>
          )}
        </div>

        {/* Upgrade Benefits (Variant B) */}
        {selectedPlan && upgradeBenefits && (
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-6 border-2 border-purple-200 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">🎉 Upgrade Benefits (Variant B)</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <Gift className="w-5 h-5" />
                  <span className="font-semibold">Operations Bonus</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  +{upgradeBenefits.operationsBonus.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">as bonus top-up</div>
              </div>

              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">Time Extension</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  +{upgradeBenefits.timeBonusDays} days
                </div>
                <div className="text-sm text-gray-600">added to validity</div>
              </div>

              <div className="bg-white p-4 rounded-lg">
                <div className="flex items-center gap-2 text-purple-600 mb-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-semibold">One-time Payment</span>
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  ₹{upgradeBenefits.chargeAmount}
                </div>
                <div className="text-sm text-gray-600">full price</div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-800 mb-3">What You'll Get:</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>{upgradeBenefits.totalOperations.toLocaleString()} total operations</span>
                  <span className="text-sm text-gray-500">
                    ({USAGE_PLANS[selectedPlan].limits.firebase_operations.toLocaleString()} new + {upgradeBenefits.operationsBonus.toLocaleString()} bonus)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>{upgradeBenefits.newValidityDays} days validity</span>
                  <span className="text-sm text-gray-500">
                    (30 days + {upgradeBenefits.timeBonusDays} bonus days)
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Valid until: {formatDate(upgradeBenefits.validUntil)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleUpgradeClick}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-blue-600 text-white py-4 rounded-lg font-bold text-lg hover:from-purple-600 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-6 h-6" />
              Upgrade to {USAGE_PLANS[selectedPlan].name}
            </button>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && selectedPlan && upgradeBenefits && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-lg w-full p-4 sm:p-6 max-h-[92dvh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Confirm Upgrade</h3>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <div className="text-center mb-4">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-gray-200 rounded font-medium">
                      {currentSubscription.basePlan?.planName || 'Current Plan'}
                    </span>
                    <ArrowRight className="w-5 h-5 text-blue-600" />
                    <span className="px-3 py-1 bg-blue-600 text-white rounded font-semibold">
                      {USAGE_PLANS[selectedPlan].name}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Operations Bonus:</span>
                    <span className="font-semibold">+{upgradeBenefits.operationsBonus.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time Bonus:</span>
                    <span className="font-semibold">+{upgradeBenefits.timeBonusDays} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">New Validity:</span>
                    <span className="font-semibold">{upgradeBenefits.newValidityDays} days</span>
                  </div>
                  <div className="border-t pt-2 mt-2 flex justify-between">
                    <span className="text-gray-800 font-semibold">Charge Amount:</span>
                    <span className="text-xl font-bold text-blue-700">₹{upgradeBenefits.chargeAmount}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Select your payment method:
              </p>

              <div className="space-y-3 mb-4">
                <button
                  onClick={() => handlePayment('razorpay')}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-5 h-5" />
                  Pay with Razorpay
                </button>

                <button
                  onClick={() => handlePayment('stripe')}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-5 h-5" />
                  Pay with Stripe
                </button>
              </div>

              <button
                onClick={() => setShowConfirmModal(false)}
                className="w-full px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanUpgradeUser;



